'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { platformFetch } from '@/lib/platform/client-headers'

/**
 * Dynamic "Sign in" / "Account" link.
 *
 * Renders "Sign in" by default (safe default, works server-side too).
 * On mount, fetches /api/platform/tier to detect session. If authenticated,
 * swaps label to "Account" and redirects to /platform/account instead of
 * /platform/sign-in.
 *
 * Used everywhere we have a sign-in affordance so the label matches state
 * universally (header, pricing page, landing page, app access gate, modals).
 *
 * Intentional design choice: "Account" not "Sign out". Sign-out lives
 * safely inside the account page to avoid misclick session loss.
 */

// The header renders this link twice (desktop bar and the CSS-hidden mobile
// menu), so every page view fired two identical session checks. Instances
// that mount while a check is in flight share it; the next mount after it
// settles asks again, so a later sign-in or sign-out is still picked up.
let inFlightCheck: Promise<boolean> | null = null

function checkSignedIn(): Promise<boolean> {
  // platformFetch attaches the localStorage token as x-platform-token; a bare
  // fetch showed token-only users (no session cookie) as signed out.
  inFlightCheck ??= platformFetch('/api/platform/tier')
    .then(r => r.json())
    .then(d => Boolean(d?.email))
    // Silent: keep the default signed-out state
    .catch(() => false)
    .finally(() => {
      inFlightCheck = null
    })
  return inFlightCheck
}

export function SignInLink({
  className,
  style,
  signedOutLabel = 'Sign in',
  signedInLabel = 'Account',
  signedOutHref = '/platform/sign-in',
  signedInHref = '/platform/account',
}: {
  className?: string
  style?: React.CSSProperties
  signedOutLabel?: string
  signedInLabel?: string
  signedOutHref?: string
  signedInHref?: string
}) {
  const [isSignedIn, setIsSignedIn] = useState(false)

  useEffect(() => {
    let cancelled = false
    void checkSignedIn().then(signedIn => {
      if (!cancelled && signedIn) setIsSignedIn(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Link
      href={isSignedIn ? signedInHref : signedOutHref}
      className={className}
      style={style}
    >
      {isSignedIn ? signedInLabel : signedOutLabel}
    </Link>
  )
}
